/**
 * useThesaurusSearch Hook
 *
 * Manages thesaurus search queries with client-side caching and loading floor.
 * Caches responses keyed by word + context to minimize API calls.
 * Enforces 300ms minimum loading state for consistent UX.
 */

import { useState, useCallback, useRef } from 'react'
import { backendApiClient } from '../lib/backendApi'
import type {
  ThesaurusIndexResponse,
  ThesaurusSynonymResponse,
  ThesaurusEntry
} from '../types/wordTools'

// Cache TTL - responses older than this trigger background refresh
const CACHE_TTL_MS = 20000 // 20 seconds

// Minimum loading display time for consistent skeleton visibility
const MIN_LOADING_MS = 300

interface CacheEntry<T> {
  data: T
  timestamp: number
}

type CurrentView = 'index' | 'synonyms'

interface UseThesaurusSearchResult {
  /** Current index search results (may be cached) */
  indexResults: ThesaurusIndexResponse | null
  /** Current synonym search results (may be cached) */
  synonymResults: ThesaurusSynonymResponse | null
  /** Is a search currently in progress? */
  isLoading: boolean
  /** Error from most recent search */
  error: string | null
  /** Is current result from cache? */
  isCached: boolean
  /** Is cached result stale (background refresh in progress)? */
  isStale: boolean
  /** Which view is currently active */
  currentView: CurrentView
  /** Execute a thesaurus index search query */
  searchIndex: (word: string, context?: string) => Promise<void>
  /** Execute a synonym search for selected entries */
  searchSynonyms: (entries: ThesaurusEntry[]) => Promise<void>
  /** Go back from synonyms view to index view */
  goBack: () => void
  /** Clear current results and cache for specific query */
  clearResults: () => void
  /** Clear entire cache (used when worksheet closes) */
  clearCache: () => void
}

/**
 * Hook for managing thesaurus search with caching and loading floor.
 *
 * Features:
 * - Client-side cache keyed by word + context
 * - 300ms minimum loading state (prevents flash on fast responses)
 * - Background refresh for stale cached entries
 * - Exposes cache state for UI indicators
 * - Manages index/synonyms view switching
 *
 * @example
 * ```typescript
 * const { indexResults, isLoading, searchIndex, currentView } = useThesaurusSearch()
 *
 * // Search thesaurus index
 * await searchIndex('love')
 *
 * // Results available with metadata
 * if (indexResults) {
 *   console.log(indexResults.entries)
 * }
 * ```
 */
export function useThesaurusSearch(): UseThesaurusSearchResult {
  // Cache storage - persists across re-renders
  const indexCacheRef = useRef<Map<string, CacheEntry<ThesaurusIndexResponse>>>(new Map())
  const synonymCacheRef = useRef<Map<string, CacheEntry<ThesaurusSynonymResponse>>>(new Map())

  // Current search state
  const [indexResults, setIndexResults] = useState<ThesaurusIndexResponse | null>(null)
  const [synonymResults, setSynonymResults] = useState<ThesaurusSynonymResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCached, setIsCached] = useState(false)
  const [isStale, setIsStale] = useState(false)
  const [currentView, setCurrentView] = useState<CurrentView>('index')

  // Track active request to prevent race conditions
  const activeRequestRef = useRef<string | null>(null)

  // Store current word for synonym searches
  const currentWordRef = useRef<string>('')

  /**
   * Generate cache key from word + context.
   * Normalized to lowercase for consistency.
   */
  const getCacheKey = useCallback((word: string, context?: string): string => {
    const normalizedWord = word.toLowerCase().trim()
    const normalizedContext = context?.toLowerCase().trim() || ''
    return normalizedContext ? `${normalizedWord}:${normalizedContext}` : normalizedWord
  }, [])

  /**
   * Check if cache entry is stale (older than TTL).
   */
  const isEntryStale = useCallback(<T,>(entry: CacheEntry<T>): boolean => {
    return Date.now() - entry.timestamp > CACHE_TTL_MS
  }, [])

  /**
   * Execute thesaurus index search with caching and loading floor.
   */
  const searchIndex = useCallback(async (word: string, context?: string): Promise<void> => {
    if (!word.trim()) {
      setIndexResults(null)
      setSynonymResults(null)
      setError(null)
      setIsCached(false)
      setIsStale(false)
      setCurrentView('index')
      currentWordRef.current = ''
      return
    }

    currentWordRef.current = word
    setCurrentView('index')
    setSynonymResults(null) // Clear synonym results when searching index

    const cacheKey = getCacheKey(word, context)
    const cachedEntry = indexCacheRef.current.get(cacheKey)

    // If we have cached data, show it immediately
    if (cachedEntry) {
      setIndexResults(cachedEntry.data)
      setIsCached(true)
      setError(null)

      const entryIsStale = isEntryStale(cachedEntry)
      setIsStale(entryIsStale)

      // If fresh enough, skip the API call
      if (!entryIsStale) {
        setIsLoading(false)
        return
      }

      // Stale data - trigger background refresh but don't show loading
    } else {
      // No cache - show loading state
      setIsLoading(true)
      setIsCached(false)
      setIsStale(false)
    }

    // Track this as the active request
    activeRequestRef.current = cacheKey

    try {
      // Start loading timer and API call simultaneously
      const loadingStart = Date.now()
      const apiPromise = backendApiClient.searchThesaurusIndex(word, context)

      // Wait for both API response and minimum loading time
      const [response] = await Promise.all([
        apiPromise,
        new Promise(resolve => {
          const elapsed = Date.now() - loadingStart
          const remaining = Math.max(0, MIN_LOADING_MS - elapsed)
          setTimeout(resolve, remaining)
        })
      ])

      // Ignore stale responses if user triggered new search
      if (activeRequestRef.current !== cacheKey) {
        return
      }

      // Update cache
      indexCacheRef.current.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      })

      // Update state
      setIndexResults(response)
      setError(null)
      setIsCached(false)
      setIsStale(false)
      setIsLoading(false)

    } catch (err) {
      // Ignore errors from stale requests
      if (activeRequestRef.current !== cacheKey) {
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to search thesaurus index'
      setError(errorMessage)
      setIsLoading(false)
      setIsStale(false)

      // Keep cached data visible on error if available
      if (!cachedEntry) {
        setIndexResults(null)
        setIsCached(false)
      }
    }
  }, [getCacheKey, isEntryStale])

  /**
   * Execute synonym search for selected entries.
   */
  const searchSynonyms = useCallback(async (entries: ThesaurusEntry[]): Promise<void> => {
    if (!currentWordRef.current || entries.length === 0) {
      setError('No word or entries selected')
      return
    }

    const word = currentWordRef.current
    setCurrentView('synonyms')

    // Generate cache key based on word + selected entry numbers
    const entryNumbers = entries.map(e => e.number).sort().join(',')
    const cacheKey = `${word}:${entryNumbers}`

    const cachedEntry = synonymCacheRef.current.get(cacheKey)

    // If we have cached data, show it immediately
    if (cachedEntry) {
      setSynonymResults(cachedEntry.data)
      setIsCached(true)
      setError(null)

      const entryIsStale = isEntryStale(cachedEntry)
      setIsStale(entryIsStale)

      if (!entryIsStale) {
        setIsLoading(false)
        return
      }
    } else {
      setIsLoading(true)
      setIsCached(false)
      setIsStale(false)
    }

    activeRequestRef.current = cacheKey

    try {
      const loadingStart = Date.now()
      const apiPromise = backendApiClient.searchThesaurusSynonyms(word, entries)

      const [response] = await Promise.all([
        apiPromise,
        new Promise(resolve => {
          const elapsed = Date.now() - loadingStart
          const remaining = Math.max(0, MIN_LOADING_MS - elapsed)
          setTimeout(resolve, remaining)
        })
      ])

      if (activeRequestRef.current !== cacheKey) {
        return
      }

      synonymCacheRef.current.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      })

      setSynonymResults(response)
      setError(null)
      setIsCached(false)
      setIsStale(false)
      setIsLoading(false)

    } catch (err) {
      if (activeRequestRef.current !== cacheKey) {
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to search synonyms'
      setError(errorMessage)
      setIsLoading(false)
      setIsStale(false)

      if (!cachedEntry) {
        setSynonymResults(null)
        setIsCached(false)
      }
    }
  }, [isEntryStale])

  /**
   * Go back from synonyms view to index view.
   */
  const goBack = useCallback(() => {
    setCurrentView('index')
    setSynonymResults(null)
    setError(null)
  }, [])

  /**
   * Clear current results (but keep cache intact).
   */
  const clearResults = useCallback(() => {
    setIndexResults(null)
    setSynonymResults(null)
    setError(null)
    setIsLoading(false)
    setIsCached(false)
    setIsStale(false)
    setCurrentView('index')
    currentWordRef.current = ''
    activeRequestRef.current = null
  }, [])

  /**
   * Clear entire cache (use when worksheet closes).
   */
  const clearCache = useCallback(() => {
    indexCacheRef.current.clear()
    synonymCacheRef.current.clear()
    clearResults()
  }, [clearResults])

  return {
    indexResults,
    synonymResults,
    isLoading,
    error,
    isCached,
    isStale,
    currentView,
    searchIndex,
    searchSynonyms,
    goBack,
    clearResults,
    clearCache
  }
}
